import {
  StoryFailure,
  type DecisionCommand,
  type MarketingDecision,
  type StoryAction,
  type StoryAdvanceReason,
  type StoryDecision,
  type Timed,
  type Visit,
} from '../domain/story'
import type {
  PendingStorage,
  StoryClock,
  StoryContext,
  StoryTransport,
} from './ports'
import { PendingDelivery } from './pending-delivery'

type Active = {
  decision: MarketingDecision
  rendered: boolean
  confirmed: boolean
}
type Options = {
  reserve: (
    command: DecisionCommand & { campaignId: string },
  ) => Promise<Timed<StoryDecision>>
  receipts: StoryTransport
  storage: PendingStorage
  clock: StoryClock
  uuid: () => string
  platform: 'web' | 'pwa'
  sourceBusy: () => boolean
  canNavigate: (path: string) => boolean
  navigate: (path: string) => void
  onError: () => void
}
export type ManualMarketingSnapshot = {
  decision: MarketingDecision | null
  ids: readonly string[]
  index: number
  visible: boolean
  busy: boolean
  ready: boolean
}

export class ManualMarketingRuntime {
  private context: StoryContext = {
    account: null,
    visible: false,
    inCabinet: false,
    allowed: false,
    blocked: false,
  }
  private session: { clientId: string; visit: Visit } | null = null
  private presenceReady = false
  private ids: string[] = []
  private requested: string[] | null = null
  private index = 0
  private active: Active | null = null
  private cache = new Map<string, Active>()
  private busy = false
  private epoch = 0
  private listeners = new Set<() => void>()
  private snapshot: ManualMarketingSnapshot = {
    decision: null,
    ids: [],
    index: 0,
    visible: false,
    busy: false,
    ready: false,
  }
  private readonly pending: PendingDelivery
  constructor(private readonly options: Options) {
    this.pending = new PendingDelivery(
      options.storage,
      options.receipts,
      options.clock,
      (receipt) => {
        for (const active of this.cache.values())
          if (active.decision.reservationId === receipt.reservationId)
            active.confirmed = true
      },
      (id) => {
        if (this.active?.decision.reservationId === id) this.close()
      },
    )
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  getSnapshot = () => this.snapshot
  isActive = () => this.ids.length > 0 || this.busy || this.requested !== null
  update(context: StoryContext) {
    if (context.account !== this.context.account) {
      this.close()
      this.session = null
      this.presenceReady = false
      this.pending.setAccount(context.account)
    }
    this.context = context
    this.refresh()
  }
  pausePresence() {
    this.presenceReady = false
    this.publish()
  }
  setVisit(clientId: string, visit: Visit | null) {
    if (!visit || (this.session && this.session.visit.id !== visit.id))
      this.close()
    this.session = visit ? { clientId, visit } : null
    this.presenceReady = visit !== null
    this.pending.wake()
    this.refresh()
  }
  open(ids: readonly string[]) {
    if (!this.canRequest() || this.isActive() || this.options.sourceBusy())
      return
    this.requested = [...new Set(ids)]
    this.refresh()
  }
  previous() {
    if (!this.busy && this.index > 0) void this.select(this.index - 1, -1)
  }
  next(reason: StoryAdvanceReason = 'tap') {
    if (
      !this.busy &&
      this.snapshot.visible &&
      this.index < this.ids.length - 1
    ) {
      if (reason === 'timer') this.enqueue('completed')
      void this.select(this.index + 1)
    }
  }
  finish() {
    if (this.index === this.ids.length - 1) this.act('completed')
  }
  displayed() {
    if (!this.snapshot.visible || !this.active || this.active.rendered) return
    if (
      !this.active.confirmed &&
      this.active.decision.expiresAt <= this.options.clock.now()
    ) {
      this.close()
      return
    }
    this.active.rendered = true
    this.enqueue(null)
  }
  act(action: StoryAction) {
    if (!this.snapshot.visible || !this.active) return
    const path = this.active.decision.story.ctaPath
    this.enqueue(action)
    this.close()
    if (action === 'cta' && this.options.canNavigate(path))
      this.options.navigate(path)
  }
  unavailable() {
    this.close()
  }
  destroy(clear: boolean) {
    this.close()
    if (clear) this.pending.setAccount(null)
    this.pending.stop()
  }
  private async select(index: number, direction = 1) {
    const session = this.session,
      epoch = this.epoch
    if (!session || !this.ready()) return
    this.busy = true
    this.publish()
    try {
      while (index >= 0 && index < this.ids.length) {
        const id = this.ids[index]!
        let active = this.cache.get(id)
        if (
          !active ||
          (!active.confirmed &&
            active.decision.expiresAt <= this.options.clock.now())
        ) {
          try {
            const result = await this.options.reserve({
              clientId: session.clientId,
              visitId: session.visit.id,
              campaignId: id,
              platform: this.options.platform,
              key: this.options.uuid(),
            })
            if (epoch !== this.epoch) return
            this.options.clock.observeServerTime?.(result.serverTime)
            if (result.value.kind !== 'marketing')
              throw new StoryFailure(404, 'CAMPAIGN_UNAVAILABLE')
            if (!this.options.canNavigate(result.value.story.ctaPath))
              throw new StoryFailure('contract', 'STORY_TARGET_INVALID')
            active = {
              decision: result.value,
              rendered: false,
              confirmed: false,
            }
            this.cache.set(id, active)
          } catch (error) {
            if (epoch !== this.epoch) return
            if (
              error instanceof StoryFailure &&
              (error.status === 404 || error.code === 'CAMPAIGN_UNAVAILABLE')
            ) {
              this.ids.splice(index, 1)
              this.cache.delete(id)
              if (direction < 0) index -= 1
              continue
            }
            throw error
          }
        }
        this.index = index
        this.active = active
        this.busy = false
        this.publish()
        return
      }
      this.close()
    } catch {
      if (epoch !== this.epoch) return
      this.close()
      this.options.onError()
    }
  }
  private enqueue(action: StoryAction | null) {
    if (!this.active || !this.context.account || !this.session) return
    this.pending.enqueue({
      account: this.context.account,
      clientId: this.session.clientId,
      reservationId: this.active.decision.reservationId,
      action,
      deadline: this.active.decision.expiresAt + 23 * 3600_000,
      impressionAccepted: this.active.confirmed,
    })
  }
  private canRequest() {
    const c = this.context
    return (
      !!c.account &&
      c.visible &&
      c.inCabinet &&
      (c.manualAllowed ?? c.allowed) &&
      this.presenceReady &&
      !!this.session
    )
  }
  private ready() {
    return this.canRequest() && !this.context.blocked
  }
  private refresh() {
    if (this.requested && this.ready() && !this.options.sourceBusy()) {
      this.ids = this.requested
      this.requested = null
      if (this.ids.length) {
        void this.select(0)
        return
      }
    }
    this.publish()
  }
  private close() {
    this.epoch += 1
    this.ids = []
    this.requested = null
    this.index = 0
    this.active = null
    this.cache.clear()
    this.busy = false
    this.publish()
  }
  private publish() {
    this.snapshot = {
      decision: this.active?.decision ?? null,
      ids: [...this.ids],
      index: this.index,
      busy: this.busy,
      ready: this.canRequest(),
      visible: !!this.active && this.ready(),
    }
    this.listeners.forEach((listener) => listener())
  }
}
